import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { Camera, Video, RotateCcw, Check, X } from 'lucide-react';

export function CameraCapture({ onCapture, mode = 'photo' }) {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [facingMode, setFacingMode] = useState('environment');
  const [error, setError] = useState(null);

  const videoConstraints = {
    facingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 },
  };

  const capturePhoto = useCallback(() => {
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot({ width: 1280, height: 720 });
    if (imageSrc) {
      setCaptured(imageSrc);
      const base64 = imageSrc.split(',')[1];
      onCapture({ data: base64, type: 'image/jpeg', preview: imageSrc });
    }
  }, [onCapture]);

  const startRecording = useCallback(() => {
    setRecordedChunks([]);
    const stream = webcamRef.current?.stream;
    if (!stream) return;
    
    const mr = new MediaRecorder(stream, { mimeType: 'video/webm' });
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => {
      if (e.data.size > 0) setRecordedChunks(prev => [...prev, e.data]);
    };
    mr.start();
    setRecording(true);
    setTimeout(() => { if (mr.state === 'recording') mr.stop(); setRecording(false); }, 15000);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }, []);

  const retake = () => {
    setCaptured(null);
    setRecordedChunks([]);
    onCapture(null);
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-muted rounded-xl text-center gap-4" data-testid="camera-error">
        <Camera className="w-12 h-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Camera access is required to submit reports.</p>
        <p className="text-xs text-muted-foreground">{error}</p>
        <Button onClick={() => setError(null)} variant="outline" size="sm">Try Again</Button>
      </div>
    );
  }

  if (captured) {
    return (
      <div className="space-y-3" data-testid="camera-preview">
        <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
          <img src={captured} alt="Captured" className="w-full h-full object-cover" />
          <div className="absolute bottom-3 right-3 flex gap-2">
            <Button size="sm" variant="destructive" onClick={retake} data-testid="camera-retake">
              <RotateCcw className="w-4 h-4 mr-1" />Retake
            </Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid="camera-confirm">
              <Check className="w-4 h-4 mr-1" />Use Photo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="camera-capture">
      <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
        <Webcam
          ref={webcamRef}
          audio={mode === 'video'}
          screenshotFormat="image/jpeg"
          screenshotQuality={0.85}
          videoConstraints={videoConstraints}
          onUserMediaError={(e) => setError(e?.message || 'Camera access denied')}
          className="w-full h-full object-cover"
        />
        {recording && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />REC
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-3">
        <Button variant="outline" size="icon" onClick={toggleCamera} data-testid="camera-flip">
          <RotateCcw className="w-4 h-4" />
        </Button>
        {mode === 'photo' ? (
          <Button size="lg" onClick={capturePhoto} className="gap-2 px-8" data-testid="camera-capture-btn">
            <Camera className="w-5 h-5" />Capture Photo
          </Button>
        ) : (
          <Button
            size="lg"
            variant={recording ? "destructive" : "default"}
            onClick={recording ? stopRecording : startRecording}
            className="gap-2 px-8"
            data-testid="camera-record-btn"
          >
            {recording ? <><X className="w-5 h-5" />Stop</> : <><Video className="w-5 h-5" />Record</>}
          </Button>
        )}
      </div>
    </div>
  );
}
